import React, { useState, useEffect } from 'react';
import { 
  Table, Badge, Spinner, Alert, 
  Button, Card, Tabs, Tab, 
  InputGroup, Form, Row, Col, Dropdown
} from 'react-bootstrap';
import { 
  FaArrowLeft, FaCalendarAlt, FaCar, 
  FaUser, FaFileExport, FaSearch,
  FaDollarSign, FaFilter
} from 'react-icons/fa';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { CSVLink } from 'react-csv';

const DriverJobs = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [driver, setDriver] = useState(null);
  const [bookings, setBookings] = useState({
    assigned: [],
    completed: [],
    cancelled: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    start: '',
    end: ''
  });
  const [exportRange, setExportRange] = useState('all');
  const [exportStatus, setExportStatus] = useState('all');

  const fetchDriverAndJobs = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      
      const [driverRes, bookingsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/admin/drivers/${id}`, {
          headers: { 'x-auth-token': token }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/admin/drivers/${id}/bookings`, {
          headers: { 'x-auth-token': token }
        })
      ]);
      
      setDriver(driverRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      console.error('Error fetching driver jobs:', err);
      setError(err.response?.data?.error || 'Failed to load driver jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverAndJobs();
  }, [id]);

  const getStatusBadge = (status) => {
    const statusMap = {
      assigned: { bg: 'primary', text: 'Assigned' },
      completed: { bg: 'success', text: 'Completed' },
      cancelled: { bg: 'danger', text: 'Cancelled' }
    };
    
    const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
    return <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const calculateRevenue = (bookings) => {
    return bookings.reduce((total, booking) => {
      const amount = parseFloat(booking.totalAmount) || 0;
      return total + amount;
    }, 0);
  };

  const getDateRange = (range) => {
    const today = new Date();
    const result = { start: '', end: '' };
    
    switch(range) {
      case '7days':
        result.start = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        break;
      case '30days':
        result.start = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
        break;
      case '2months':
        result.start = new Date(today.setMonth(today.getMonth() - 2)).toISOString().split('T')[0];
        break;
      case '6months':
        result.start = new Date(today.setMonth(today.getMonth() - 6)).toISOString().split('T')[0];
        break;
      case '1year':
        result.start = new Date(today.setFullYear(today.getFullYear() - 1)).toISOString().split('T')[0];
        break;
      case 'custom':
        // Use existing dateFilter values
        return dateFilter;
      default:
        // 'all' - no date filter
        return { start: '', end: '' };
    }
    
    result.end = new Date().toISOString().split('T')[0];
    return result;
  };

  const filterBookings = (bookings, statusFilter = 'all', dateRange = 'all') => {
    const range = getDateRange(dateRange);
    
    return bookings.filter(booking => {
      // Status filter
      if (statusFilter !== 'all' && booking.bookingStatus !== statusFilter) {
        return false;
      }
      
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (booking.customerName?.toLowerCase().includes(searchLower)) ||
        (booking.customerPhone?.includes(searchTerm)) ||
        (booking.vehicleName?.toLowerCase().includes(searchLower));
      
      // Date filter
      try {
        const bookingDate = booking.pickupDate ? new Date(booking.pickupDate) : null;
        const startDate = range.start ? new Date(range.start) : null;
        const endDate = range.end ? new Date(range.end) : null;
        
        const matchesDate = (
          (!startDate || (bookingDate && bookingDate >= startDate)) && 
          (!endDate || (bookingDate && bookingDate <= endDate))
        );
        
        return matchesSearch && matchesDate;
      } catch {
        return matchesSearch;
      }
    });
  };

  const prepareCSVData = () => {
    let bookingsToExport = [];
    const statusFilter = exportStatus === 'all' ? 'all' : exportStatus;
    
    if (exportStatus === 'all') {
      bookingsToExport = [
        ...filterBookings(bookings.assigned, 'all', exportRange),
        ...filterBookings(bookings.completed, 'all', exportRange),
        ...filterBookings(bookings.cancelled, 'all', exportRange)
      ];
    } else {
      bookingsToExport = filterBookings(bookings[exportStatus], 'all', exportRange);
    }
    
    return bookingsToExport.map(booking => ({
      'Booking ID': booking._id,
      'Customer': booking.customerName || 'N/A',
      'Phone': booking.customerPhone || 'N/A',
      'Pickup Date': formatDate(booking.pickupDate),
      'Pickup Time': booking.pickupTime || 'N/A',
      'Vehicle': booking.vehicleName || 'N/A',
      'Amount': booking.totalAmount ? `$${(parseFloat(booking.totalAmount)).toFixed(2)}` : '$0.00',
      'Status': booking.bookingStatus || 'N/A'
    }));
  };

  const filteredAssigned = filterBookings(bookings.assigned);
  const filteredCompleted = filterBookings(bookings.completed);
  const filteredCancelled = filterBookings(bookings.cancelled);

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-4">
        {error}
      </Alert>
    );
  }

  return (
    <div className="driver-jobs-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/admin/drivers')}
            className="me-3"
          >
            <FaArrowLeft />
          </Button>
          Driver Job History: {driver?.name || 'Unknown Driver'}
        </h2>
      </div>

      <Card>
        <Card.Body>
          {/* Search and Filter Section */}
          <Row className="mb-4 g-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by customer, phone, or vehicle"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Control
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
              />
            </Col>
          </Row>

          {/* Revenue Summary */}
          <Row className="mb-3">
            <Col>
              <div className="revenue-summary p-3 bg-light rounded mb-3">
                <h5 className="d-flex align-items-center">
                  <FaDollarSign className="me-2" />
                  Revenue Summary
                </h5>
                <div className="d-flex justify-content-between flex-wrap">
                  <span className="me-3">Assigned: <strong>${calculateRevenue(filteredAssigned).toFixed(2)}</strong></span>
                  <span className="me-3">Completed: <strong>${calculateRevenue(filteredCompleted).toFixed(2)}</strong></span>
                  <span>Cancelled: <strong>${calculateRevenue(filteredCancelled).toFixed(2)}</strong></span>
                </div>
              </div>
            </Col>
          </Row>

          {/* Export Section */}
          <Row className="mb-3">
            <Col md={4}>
              <Dropdown className="mb-2">
                <Dropdown.Toggle variant="secondary" id="dropdown-range">
                  <FaFilter className="me-2" />
                  {exportRange === 'all' ? 'All Time' : 
                   exportRange === '7days' ? 'Last 7 Days' :
                   exportRange === '30days' ? 'Last 30 Days' :
                   exportRange === '2months' ? 'Last 2 Months' :
                   exportRange === '6months' ? 'Last 6 Months' :
                   exportRange === '1year' ? 'Last Year' : 'Custom Range'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setExportRange('all')}>All Time</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportRange('7days')}>Last 7 Days</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportRange('30days')}>Last 30 Days</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportRange('2months')}>Last 2 Months</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportRange('6months')}>Last 6 Months</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportRange('1year')}>Last Year</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportRange('custom')}>Custom Range</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col md={4}>
              <Dropdown className="mb-2">
                <Dropdown.Toggle variant="secondary" id="dropdown-status">
                  <FaFilter className="me-2" />
                  {exportStatus === 'all' ? 'All Statuses' : 
                   exportStatus === 'assigned' ? 'Assigned Only' :
                   exportStatus === 'completed' ? 'Completed Only' :
                   'Cancelled Only'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setExportStatus('all')}>All Statuses</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportStatus('assigned')}>Assigned Only</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportStatus('completed')}>Completed Only</Dropdown.Item>
                  <Dropdown.Item onClick={() => setExportStatus('cancelled')}>Cancelled Only</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col md={4} className="text-end">
              <CSVLink
                data={prepareCSVData()}
                filename={`${driver?.name?.replace(/\s+/g, '-') || 'driver'}-jobs-${exportStatus}-${exportRange}-${new Date().toISOString().slice(0,10)}.csv`}
                className="btn btn-success"
                target="_blank"
              >
                <FaFileExport className="me-2" />
                Export to CSV
              </CSVLink>
            </Col>
          </Row>

          {/* Tabs */}
          <Tabs defaultActiveKey="assigned" id="driver-jobs-tabs" className="mb-3">
            <Tab eventKey="assigned" title={
              <span className="d-flex align-items-center">
                Assigned <Badge bg="primary" className="ms-2">{filteredAssigned.length}</Badge>
                <span className="ms-2">(${calculateRevenue(filteredAssigned).toFixed(2)})</span>
              </span>
            }>
              <JobTable 
                bookings={filteredAssigned} 
                emptyMessage="No assigned bookings found"
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
              />
            </Tab>
            
            <Tab eventKey="completed" title={
              <span className="d-flex align-items-center">
                Completed <Badge bg="success" className="ms-2">{filteredCompleted.length}</Badge>
                <span className="ms-2">(${calculateRevenue(filteredCompleted).toFixed(2)})</span>
              </span>
            }>
              <JobTable 
                bookings={filteredCompleted} 
                emptyMessage="No completed bookings found"
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
              />
            </Tab>
            
            <Tab eventKey="cancelled" title={
              <span className="d-flex align-items-center">
                Cancelled <Badge bg="danger" className="ms-2">{filteredCancelled.length}</Badge>
                <span className="ms-2">(${calculateRevenue(filteredCancelled).toFixed(2)})</span>
              </span>
            }>
              <JobTable 
                bookings={filteredCancelled} 
                emptyMessage="No cancelled bookings found"
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
              />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

const JobTable = ({ bookings, emptyMessage, formatDate, getStatusBadge }) => {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Booking ID</th>
            <th>Customer</th>
            <th>Pickup Date</th>
            <th>Vehicle</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking) => (
            <tr key={booking._id}>
              <td>{booking._id}</td>
              <td>
                <div className="d-flex align-items-center">
                  <FaUser className="me-2" />
                  <div>
                    <div>{booking.customerName || 'N/A'}</div>
                    <small className="text-muted">{booking.customerPhone || 'N/A'}</small>
                  </div>
                </div>
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <FaCalendarAlt className="me-2" />
                  {formatDate(booking.pickupDate)}
                </div>
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <FaCar className="me-2" />
                  {booking.vehicleName || 'N/A'}
                </div>
              </td>
              <td>${(parseFloat(booking.totalAmount) || 0).toFixed(2)}</td>
              <td>{getStatusBadge(booking.bookingStatus)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default DriverJobs;